import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, ArrowRight, Sparkles, AlertTriangle } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

// Detect if user is using a recommended browser (Chrome or Edge)
const isRecommendedBrowser = () => {
  const ua = navigator.userAgent;
  const isChrome = /Chrome/.test(ua) && !/Edg/.test(ua);
  const isEdge = /Edg/.test(ua);
  return isChrome || isEdge;
};

export function LoginPage() {
  const navigate = useNavigate();
  const { login, setRememberMe } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMagicLinkSent, setIsMagicLinkSent] = useState(false);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [rememberMe, setRememberMeLocal] = useState(true);
  const showBrowserWarning = !isRecommendedBrowser();

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      toast.error('Please enter your password');
      return;
    }
    setIsLoading(true);

    try {
      setRememberMe(rememberMe);
      await login(email, password, rememberMe);
      toast.success('Welcome back!');
      navigate('/');
    } catch {
      toast.error('Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/auth/magic-link', { email, rememberMe });
      setIsMagicLinkSent(true);
      // Store rememberMe preference for when magic link is verified
      localStorage.setItem('yoda-remember-me-pending', rememberMe ? 'true' : 'false');
      toast.success('Magic link sent! Check your email.');
    } catch {
      toast.error('Failed to send magic link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRememberMeChange = (checked: boolean) => {
    setRememberMeLocal(checked);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-bg via-bg to-accent/5" />
        <motion.div
          className="absolute top-1/3 -left-20 w-80 h-80 bg-accent/20 rounded-full blur-3xl"
          animate={{ x: [0, 40, 0], y: [0, -30, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-20 w-96 h-96 bg-purple-500/15 rounded-full blur-3xl"
          animate={{ x: [0, -40, 0], y: [0, 30, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, type: 'spring' }}
          className="text-center mb-8"
        >
          <img
            src="/OMLYodaLogo.png"
            alt="OML Yoda"
            className="h-24 mx-auto"
          />
        </motion.div>

        {/* Login card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/80 dark:bg-[#1a1a1f]/80 backdrop-blur-xl rounded-3xl border border-border/50 p-8 shadow-xl"
        >
          {isMagicLinkSent ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-6"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                <Mail className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Check your email</h2>
              <p className="text-text-secondary">
                We've sent a magic link to <strong>{email}</strong>
              </p>
              <button
                onClick={() => setIsMagicLinkSent(false)}
                className="mt-4 text-accent hover:underline"
              >
                Use a different email
              </button>
            </motion.div>
          ) : (
            <div className="space-y-5">
              {/* Email field - always visible */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-text-secondary">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-bg-tertiary border border-border text-text placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                    required
                  />
                </div>
              </div>

              {/* Magic Link Button - shown by default */}
              <AnimatePresence mode="wait">
                {!showPasswordLogin && (
                  <motion.div
                    key="magic-link"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <motion.button
                      type="button"
                      onClick={handleMagicLink}
                      disabled={isLoading}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-3 rounded-xl bg-gradient-to-r from-accent to-accent-light text-white font-medium flex items-center justify-center gap-2 shadow-lg shadow-accent/25 disabled:opacity-50"
                    >
                      {isLoading ? (
                        'Sending...'
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Send Magic Link
                        </>
                      )}
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Password Section */}
              <AnimatePresence mode="wait">
                {showPasswordLogin ? (
                  <motion.form
                    key="password-form"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    onSubmit={handlePasswordLogin}
                    className="space-y-4"
                  >
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-text-secondary">
                        Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          autoFocus
                          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-bg-tertiary border border-border text-text placeholder:text-text-muted focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                        />
                      </div>
                    </div>

                    <motion.button
                      type="submit"
                      disabled={isLoading}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-3 rounded-xl bg-gradient-to-r from-accent to-accent-light text-white font-medium flex items-center justify-center gap-2 shadow-lg shadow-accent/25 disabled:opacity-50"
                    >
                      {isLoading ? 'Signing in...' : 'Sign In'}
                      <ArrowRight className="w-4 h-4" />
                    </motion.button>

                    <button
                      type="button"
                      onClick={() => setShowPasswordLogin(false)}
                      className="w-full text-center text-sm text-text-muted hover:text-accent transition-colors"
                    >
                      ← Use magic link instead
                    </button>
                  </motion.form>
                ) : (
                  <motion.button
                    key="show-password"
                    type="button"
                    onClick={() => setShowPasswordLogin(true)}
                    className="w-full text-center text-sm text-text-muted hover:text-accent transition-colors"
                  >
                    or enter your password
                  </motion.button>
                )}
              </AnimatePresence>

              {/* Remember Me Checkbox */}
              <div className="pt-2">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => handleRememberMeChange(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-5 h-5 rounded-md border-2 border-border bg-bg-tertiary peer-checked:bg-accent peer-checked:border-accent transition-all flex items-center justify-center group-hover:border-accent/50">
                      {rememberMe && (
                        <motion.svg
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-3 h-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </motion.svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-text-secondary group-hover:text-text transition-colors">
                    Keep me logged in
                  </span>
                </label>
              </div>
            </div>
          )}
        </motion.div>

        {showBrowserWarning && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-200">
                For the best experience, we recommend using <strong>Google Chrome</strong> or <strong>Microsoft Edge</strong>.
              </p>
            </div>
          </motion.div>
        )}

        <p className="text-center text-sm text-text-muted mt-6">
          Powered by OML Design
        </p>
      </motion.div>
    </div>
  );
}
